import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { DailyReportService } from '../../../../daily-report.service';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { Router } from '@angular/router';
import {
  FetchAllBidItemsRequest,
  FetchLabNameListRequest,
  FetchMaterialTestNameListRequest,
  SaveExternalTestRequest,
} from '../../../../../../../../core/projects.actions';
import { allBidItemsFilter, labNameList, materialTestNameList } from '../../../../../../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';

@Component({
    selector: 'app-create-external-test',
    templateUrl: './create-external-test.component.html',
    styleUrls: ['./create-external-test.component.scss'],
    standalone: false
})
export class CreateExternalTestComponent implements OnInit, OnDestroy {
  @Input() data: any;

  form = new FormGroup({
    bidItem: new FormControl({ value: null, disabled: false }, [Validators.required]),
    testName: new FormControl({ value: null, disabled: false }, [Validators.required]),
    labName: new FormControl({ value: null, disabled: false }, [Validators.required]),
    date: new FormControl({ value: null, disabled: false }, [Validators.required]),
    time: new FormControl({ value: null, disabled: false }, [Validators.required]),
    fromTime: new FormControl({ value: null, disabled: false }),
    toTime: new FormControl({ value: null, disabled: false }),
    fromTimePeriod: new FormControl({ value: null, disabled: false }),
    toTimePeriod: new FormControl({ value: null, disabled: false }),
    longitude: new FormControl({ value: null, disabled: false }),
    latitude: new FormControl({ value: null, disabled: false }),
    pictures: new FormControl({ value: null, disabled: false }),
    videos: new FormControl({ value: null, disabled: false }),
    comments: new FormControl({ value: null, disabled: false }),
  });
  errorMessages: any = {};
  errorMessageMap = {
    bidItem: {
      required: 'This field is required.',
    },
    testName: {
      required: 'This field is required.',
    },
    labName: {
      required: 'This field is required.',
    },
    date: {
      required: 'This field is required.',
    },
    time: {
      required: 'This field is required.',
    },
    fromTime: {
      required: 'This field is required.',
    },
    toTime: {
      required: 'This field is required.',
    },
    location: {
      required: 'This field is required.',
    },
    pictures: {
      required: 'This field is required.',
    },
    videos: {
      required: 'This field is required.',
    },
    comments: {
      required: 'This field is required.',
    },
  };
  bidItemOptions: any = [];
  testNameOptions: any = [];
  labNameOptions: any = [];
  projectId: string;
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  displayCommentSection = false;
  siteId: any;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store,
    private commonService: CommonService,
    private dailyReportService: DailyReportService,
    private projectsService: ProjectsService,
    private cdRef: ChangeDetectorRef,
    private router: Router,
  ) {

  }

  ngOnInit() {
    const urlWithoutQp = this.router.url.split('?')[0];
    const segments = urlWithoutQp.split('/');
    this.projectId = segments[2];
    this.siteId = segments[4];

    if (this.projectId) {
      this.store.dispatch(FetchAllBidItemsRequest({
        payload: {
          projectId: this.projectId,
          qp: {
            include: [
              {
                bid_items: ['fields'],
              },
            ],
          },
        },
      }));

      this.store.dispatch(FetchMaterialTestNameListRequest({
        payload: {
          projectId: this.projectId,
          qp: {
            start: 0,
            total: 1000,
          },
        },
      }));

      this.store.dispatch(FetchLabNameListRequest({
        payload: {
          qp: {
            projectId: this.projectId,
            start: 0,
            total: 1000,
          },
        },
      }));
    }

    this.dailyReportService.disableOptionMenu.next(true);

    this.store.select(allBidItemsFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        this.bidItemOptions = bidItems || [];
      });

    this.store.select(materialTestNameList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((list) => {
        this.testNameOptions = list || [];
      });

    this.store.select(labNameList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((list) => {
        this.labNameOptions = list || [];

        if (this.labNameOptions.length === 1) {
          this.form.patchValue({
            labName: this.labNameOptions[0].value,
          }, { emitEvent: false });
        }
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.dailyReportService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);
            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array

          } else if (eachItem?.['fileType'] === 'video') {
            let videos = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form?.get('videos')?.setValue(JSON.stringify(videos));
            this.mediaFiles.push(eachItem);  // Add to mediaFiles array
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });

    this.dailyReportService.addComment
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.displayCommentSection = true;
        this.cdRef.detectChanges();
      });

    this.dailyReportService.selectedLocation
      .pipe(takeUntil(this.onDestroy))
      .subscribe((location) => {
        this.form.patchValue({
          latitude: location.latitude,
          longitude: location.longitude,
        }, { emitEvent: false });
        this.cdRef.detectChanges();
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  save() {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const formValues = this.form.getRawValue();

    const payload = {
      bid_item_id: formValues?.bidItem,
      comment: formValues?.comments,
      lab_id: formValues?.labName,
      latitude: formValues?.latitude,
      longitude: formValues?.longitude,
      material_test_id: formValues?.testName,
      pictures: JSON.parse(formValues?.pictures)?.map(o => o.id),
      request_date: this.getISO(formValues),
      request_interval: this.getInterval(formValues),
      siteId: this.siteId,
    };

    this.store.dispatch(SaveExternalTestRequest({ payload }));
  }

  getISO(formValues) {
    const { date, fromTime, fromTimePeriod } = formValues;

    const [h, m = 0] = fromTime.split(':').map(Number);

    let hour = h;
    if (fromTimePeriod === 'PM' && h !== 12) {
      hour += 12;
    }
    if (fromTimePeriod === 'AM' && h === 12) {
      hour = 0;
    }

    const userTZ = moment.tz.guess();

    const finalDate = moment(date)
      .tz(userTZ)
      .hour(hour)
      .minute(m)
      .second(0)
      .millisecond(0);

    return finalDate.format();
  }

  getInterval(formValues) {
    const { date, fromTime, fromTimePeriod, toTime, toTimePeriod } = formValues;

    if (!toTime || !toTimePeriod) {
      return null;
    }

    // Convert 12h → 24h
    const to24 = (time, period) => {
      // tslint:disable-next-line:prefer-const
      let [h, m = 0] = time.split(':').map(Number);
      if (period === 'PM' && h !== 12) {
        h += 12;
      }
      if (period === 'AM' && h === 12) {
        h = 0;
      }
      return { h, m };
    };

    const from = to24(fromTime, fromTimePeriod);
    const to = to24(toTime, toTimePeriod);

    // date is a moment object
    const start = moment(date).hour(from.h).minute(from.m).second(0).millisecond(0);
    const end = moment(date).hour(to.h).minute(to.m).second(0).millisecond(0);

    const diffMinutes = end.diff(start, 'minutes');

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    // Build ISO duration
    let iso = 'PT';
    if (hours) {
      iso += `${hours}H`;
    }
    if (minutes) {
      iso += `${minutes}M`;
    }

    return iso;
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  showLocationSection() {
    return this.form?.controls?.latitude?.value !== null &&
      this.form?.controls?.longitude?.value !== null &&
      this.form?.controls?.latitude?.value !== '' &&
      this.form?.controls?.longitude?.value !== '';
  }

  deleteLocation() {
    this.form?.get('latitude').setValue(null);
    this.form?.get('longitude').setValue(null);
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
